import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  Button,
  Divider,
  Link,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { IStationeryPurchaseOrderDetailedItem } from 'src/types/stationery-purchase-order';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemCode', label: 'Item Code', align: 'left', primary: true },
  { id: 'itemName', label: 'Item Name', align: 'left', primary: true, minWidth: 260 },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
  { id: 'unitPrice', label: 'Unit Cost', align: 'right', primary: true },
  { id: 'discount', label: 'Discount', align: 'right', primary: true },
  { id: 'total', label: 'Total', align: 'right', primary: true },
];

type Props = {
  currentPurchaseOrder: IStationeryPurchaseOrderDetailedItem;
};

export default function StationeryPurchaseOrderDetails({ currentPurchaseOrder }: Props) {
  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid
              mb={1}
              display="flex"
              mt={2}
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              <Typography variant="subtitle2">Basic Information</Typography>
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={currentPurchaseOrder?.orderCode}
                secondary="Order No"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fDateTime(currentPurchaseOrder?.purchaseDate)}
                secondary="Purchase Date"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            {currentPurchaseOrder?.supplier && (
              <Grid xs={12} md={4}>
                <ListItemText
                  primary={
                    <Link
                      component={RouterLink}
                      href={paths.dashboard.suppliers.view(currentPurchaseOrder.supplier._id)}
                    >
                      {currentPurchaseOrder.supplier.supplierName}
                    </Link>
                  }
                  secondary="Supplier"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            )}

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fCurrencyRupees(Number(currentPurchaseOrder?.discount ?? 0) * 100)}
                secondary="Discount"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fCurrencyRupees(
                  Number(currentPurchaseOrder?.totalAfterDiscount ?? 0) * 100
                )}
                secondary="Grand Total"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            {currentPurchaseOrder?.remarks && (
              <Grid xs={12}>
                <ListItemText
                  primary={currentPurchaseOrder.remarks}
                  secondary="Remarks"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            )}

            <Divider
              sx={{
                width: 1,
                my: 2,
                borderStyle: 'dashed',
              }}
            />

            <Grid mb={1} xs={12} width="full">
              <Typography variant="subtitle2">Line Items</Typography>
            </Grid>

            <Grid xs={12}>
              <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                <Scrollbar>
                  <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
                    <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                    <TableBody>
                      {currentPurchaseOrder?.items.map((item) => (
                        <TableRow hover key={item._id}>
                          <TableCell align="left">
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.stationeryItem.view(item.stationeryItem._id)}
                            >
                              {item.stationeryItem.itemCode}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.stationeryItem.itemName}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.quantity}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {fCurrencyRupees((item.unitPrice ?? 0) * 100)}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {fCurrencyRupees((item.discount ?? 0) * 100)}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {fCurrencyRupees((item.totalAfterDiscount ?? 0) * 100)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={3}>
            <Link component={RouterLink} href={paths.dashboard.stationeryPurchaseOrder.root}>
              <Button variant="outlined" color="inherit">
                Back
              </Button>
            </Link>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
